import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { compose } from "redux";
import { isActiveMembership } from "helpers/membership-helpers";
import { DiscoSearchServiceClient } from "../clients/disco-search-service-client";
import {
  mapGridQueryToDiscoQuery,
  setFilter,
  mapWorkingFiltersToGridQuery,
  addUserId,
} from "../helpers/disco-filter-helpers";
import { getUserId } from "components/source/hoc/with-user-data";
import { DISCO_CATALOG_PRODUCT_SCOPES } from "rtr-constants";

const initialStateProducts = {
  availabilityScope: null,
  gridProducts: [],
  gridCount: 0,
  gridLoading: true,
  error: null,
  // differentiating filters that disco returns from static filters
  informedFilters: [],
};

const initialStateByStyleName = {
  products: [],
  error: null,
};

/**
 * @callback ProductsFetchFunction
 * @param {{query: object, page: number, limit: number, lens?: string, shortlistId?: string, userId?: string, productScope?: string | null, version?: string, isActiveMember?: boolean, availabilityScope?: import("types/disco-search-type").AvailabilityScopeType }} params - The parameters for fetching products.
 * @returns {Promise<any>} - A promise that resolves to the fetched products.
 */

/**
 * @type {Record<import("components/types/disco-search-type").DiscoSearchType, ProductsFetchFunction>}
 */
export const fetchProductsFns = {
  clearance: ({ query, page, limit }) => DiscoSearchServiceClient.getInstance().clearanceSearch(query, page, limit),
  curation: ({ query, page, limit, lens }) =>
    DiscoSearchServiceClient.getInstance().curationSearch(query, page, limit, lens),
  designer: ({ query, page, limit, lens }) =>
    DiscoSearchServiceClient.getInstance().designerSearch(query, page, limit, lens),
  designerCategory: ({ query, page, limit, lens, availabilityScope, isActiveMember }) =>
    DiscoSearchServiceClient.getInstance().catalogSearch({
      query,
      page,
      limit,
      lens,
      availabilityScope,
      productScope: DISCO_CATALOG_PRODUCT_SCOPES.DESIGNER,
      isActiveMember,
    }),
  category: ({ query, page, limit, lens }) =>
    DiscoSearchServiceClient.getInstance().categorySearch(query, page, limit, lens),
  shortlist: ({ limit, page, query, userId, availabilityScope }) =>
    DiscoSearchServiceClient.getInstance().shortlist(query, page, limit, availabilityScope, userId),
  view_all: ({ query, page, limit, lens }) =>
    DiscoSearchServiceClient.getInstance().viewAllSearch(query, page, limit, lens),
  user: ({ query, page, limit, lens, userId, productScope, isActiveMember, headers, availabilityScope }) =>
    DiscoSearchServiceClient.getInstance().userProductSearch({
      query,
      page,
      limit,
      lens,
      userId,
      productScope,
      isActiveMember,
      headers,
      availabilityScope,
    }),
  recommendation: ({ query, page, limit, lens, userId, productScope, isActiveMember, headers, availabilityScope }) =>
    DiscoSearchServiceClient.getInstance().recommendationProductSearch({
      query,
      page,
      limit,
      lens,
      userId,
      productScope,
      isActiveMember,
      headers,
      availabilityScope,
    }),
};

export const fetchProducts = createAsyncThunk("products/fetchProducts", async (page, { getState }) => {
  const state = getState();

  const lens = state.userData?.lens;
  const { productScope, availabilityScope, membershipState } = state;
  const userId = getUserId(state.userData);
  const path = state?.nextGridType === "category" ? state?.path?.split("?")[0] : null; // needed for taxonomy updates
  const customerCategory = path?.replace(/^\/c\//, "") || null; // remove leading /c/
  const isActiveMember = isActiveMembership(membershipState);

  const query = compose(
    setFilter("availableOnly", state.workingFilters?.availableOnly),
    addUserId(userId),
    query => (customerCategory ? { ...query, customerCategory } : query),
    filters => mapGridQueryToDiscoQuery(filters, state.nextGridType),
    setFilter("sort", state.workingSort),
    mapWorkingFiltersToGridQuery
  )(state.workingFilters);

  const fetchParams = {
    availabilityScope,
    lens,
    limit: void 0,
    page,
    productScope,
    query,
    userId,
    isActiveMember,
  };

  if (state.nextGridType === "shortlist") {
    fetchParams.shortlistId = state.shortlistId;
  }

  return fetchProductsFns[state.nextGridType](fetchParams);
});

export const fetchProductsByStyleNames = createAsyncThunk("products/fetchProductsByStyleNames", async params => {
  const clientResponse = DiscoSearchServiceClient.getInstance().productsDataByStyleNames(params);
  return clientResponse;
});

export const productsSlice = createSlice({
  name: "products",
  initialState: initialStateProducts,
  reducers: {
    updateAvailabilityScope: (state, action) => {
      state.availabilityScope = action.payload;
    },
  },
  extraReducers: builder => {
    builder.addCase(fetchProducts.fulfilled, (state, action) => {
      state.gridProducts = action.payload.items; // Assign action.payload directly to gridProducts
      state.gridCount = action.payload.count.total;
      state.informedFilters = action.payload.filters;
      state.assortment = action.payload?.meta?.assortment || [];
    });
  },
});

export const productsSliceByStyleName = createSlice({
  name: "products",
  initialState: initialStateByStyleName,
  extraReducers: builder => {
    builder.addCase(fetchProductsByStyleNames.fulfilled, (state, action) => {
      state.products = action.payload.items;
    });
  },
});

export const productSliceReducer = {
  // ReduxApp expects reducers to have a "types" property
  types: ["products/fetchProducts/fulfilled", "products/updateAvailabilityScope"],
  fn: productsSlice.reducer,
};

export const { updateAvailabilityScope } = productsSlice.actions;

export const productSliceByStyleNames = {
  // ReduxApp expects reducers to have a "types" property
  types: ["products/fetchProductsByStyleNames/fulfilled"],
  fn: productsSliceByStyleName.reducer,
};
